<template>
  <!-- width:100% na raiz: a instância ocupa a largura total do contêiner
       pai (mesma dos campos de e-mail/senha), mesmo quando o pai é um
       flex que "encolhe ao conteúdo" (ex.: .account-google-wrapper). -->
  <div style="width: 100%">
    <div class="g-id-slot" style="width: 100%" v-show="!unavailable"></div>
    <p v-if="unavailable" class="google-login-unavailable">
      Login com Google indisponível no momento. Use e-mail e senha abaixo.
    </p>
  </div>
</template>

<script>
import axios from "axios";
import { getPublicApiConfig } from "@/services/apiConfig";

// Valor de fallback, usado caso a chave ainda não tenha sido configurada na
// tela "Configuração" do Gerenciador de Produtos (ou o endpoint esteja
// indisponível no momento).
const GOOGLE_CLIENT_ID_FALLBACK = "708985352395-rnaek1502u90siljbhvoiqdob6aq91o9.apps.googleusercontent.com";

// O script do Google (accounts.google.com/gsi/client) é carregado com
// async/defer no index.html, então pode ainda não estar disponível no
// momento em que este componente monta. Aguarda até `maxTentativas` vezes
// (a cada 100ms) antes de desistir.
function aguardarGoogleGsi(maxTentativas = 40, intervaloMs = 100) {
  return new Promise((resolve, reject) => {
    let tentativas = 0;
    const checar = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        resolve(window.google);
        return;
      }
      tentativas += 1;
      if (tentativas >= maxTentativas) {
        reject(new Error("Script do Google (gsi/client) não carregou a tempo."));
        return;
      }
      setTimeout(checar, intervaloMs);
    };
    checar();
  });
}

export default {
  name: "GoogleLogin",
  data() {
    return {
      unavailable: false,
      googleGsi: null,
      ultimoLarguraBotaoGoogle: 0,
      resizeObserverGoogle: null,
    };
  },
  beforeDestroy() {
    if (this.resizeObserverGoogle) {
      this.resizeObserverGoogle.disconnect();
      this.resizeObserverGoogle = null;
    }
    document.removeEventListener('shown.bs.dropdown', this.onDropdownShown);
  },
  methods: {
    async sendToBackend(token) {
      console.log("ENVIANDO AO BACKEND", token);
      try {
        const res = await axios.post(window.location.origin + "/api/google_login.php", { token });
        console.log("Login backend:", res.data);

        localStorage.setItem("accessToken", res.data.access);
        this.$api.getAddon();
        this.$router.push("/");
      } catch (e) {
        console.error("Erro backend", e);
      }
    },

    async handleCredentialResponse(response) {
      console.log("Google response:", response);
      const token = response.credential;
      await this.sendToBackend(token);
    },

    // Reforço ao ResizeObserver: quando o menu de conta termina de abrir,
    // o Bootstrap dispara "shown.bs.dropdown" - aí o contêiner já tem o
    // layout final e o botão pode ser re-renderizado com a largura certa.
    onDropdownShown() {
      setTimeout(() => this.renderizarBotaoSePossivel(), 60);
    },

    // O parâmetro "width" do Google só aceita um número em pixels (não
    // aceita "100%"). A largura certa é a do slot medida COM o layout já
    // aplicado - por isso o botão é (re)renderizado quando o contêiner
    // fica visível, e não apenas na montagem do componente (quando o
    // dropdown do menu de conta ainda está display:none e o slot mede
    // 0px, o que deixava o botão na largura fallback do Google, menor
    // que os campos de e-mail/senha). Cada instância deste componente tem
    // seu próprio slot (CLASSE, não id - com id, todas as instâncias
    // renderizavam no primeiro contêiner do documento, que era o do
    // cabeçalho escondido).
    slotProprio() {
      return this.$el ? this.$el.querySelector(".g-id-slot") : null;
    },

    renderizarBotaoSePossivel() {
      if (!this.googleGsi || this.unavailable) return;
      const slot = this.slotProprio();
      if (!slot) return;
      const largura = Math.round(this.$el.getBoundingClientRect().width);
      if (largura <= 0) return;
      if (Math.abs(largura - this.ultimoLarguraBotaoGoogle) <= 8) return;
      slot.innerHTML = "";
      this.ultimoLarguraBotaoGoogle = largura;
      this.googleGsi.accounts.id.renderButton(slot, {
        theme: "outline",
        size: "large",
        width: largura,
      });
    },
  },

  async mounted() {
    let clientId = GOOGLE_CLIENT_ID_FALLBACK;

    try {
      const config = await getPublicApiConfig();
      const configuredClientId = config?.login_social?.google?.client_id;
      if (configuredClientId) {
        clientId = configuredClientId;
      }
    } catch (e) {
      console.warn("Não foi possível obter o Client ID do Google via Gerenciador de Produtos, usando fallback.", e);
    }

    try {
      const google = await aguardarGoogleGsi();

      google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleCredentialResponse,
        // Reporta erros de inicialização (ex.: domínio não autorizado para
        // este Client ID) em vez de deixá-los silenciosos no console interno
        // do Google. Esse erro em especial ("The given origin is not
        // allowed for the given client ID" / "origin_mismatch") NÃO é um
        // bug de código: ele é resolvido cadastrando o domínio do site
        // (ex.: https://www.mx3network.com.br E https://mx3network.com.br,
        // com e sem "www") em "APIs & Services > Credentials > OAuth 2.0
        // Client ID > Authorized JavaScript origins" no Google Cloud
        // Console, para o Client ID configurado na tela "Configuração" do
        // Gerenciador de Produtos.
        error_callback: (err) => {
          console.warn("[GoogleLogin] Falha ao inicializar o login do Google (verifique 'Authorized JavaScript origins' no Google Cloud Console para este domínio):", err);
          this.unavailable = true;
        },
        context: "signin",
      });

      this.googleGsi = google;
      this.renderizarBotaoSePossivel();

      // Re-renderiza com a largura REAL sempre que o contêiner muda de
      // tamanho (ex.: dropdown do menu de conta saindo de display:none).
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserverGoogle = new ResizeObserver(() => {
          this.renderizarBotaoSePossivel();
        });
        this.resizeObserverGoogle.observe(this.$el);
      }
      document.addEventListener('shown.bs.dropdown', this.onDropdownShown);
    } catch (e) {
      // Script do Google não carregou (bloqueado por extensão/adblock,
      // rede lenta, etc.) - não deixa a página de login/cadastro quebrada.
      console.warn("[GoogleLogin] Login com Google indisponível nesta sessão:", e);
      this.unavailable = true;
    }
  },
};
</script>

<style scoped>
.g-id-slot {
  width: 100%;
}

.google-login-unavailable {
  font-size: 0.8rem;
  color: #8a8f94;
  text-align: center;
  margin: 8px 0 0 0;
}
</style>
